import React, { useEffect, useMemo, useState } from "react";
import { Field, Form, Formik } from "formik";
import * as Yup from "yup";
import classNames from "classnames";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import Modal from "../components/Modal/Modal";
import Select from "../components/Select/Select";
import Input from "../components/Input/Input";
import Textarea from "../components/Textarea/Textarea";
import DatePickerField from "../components/FormikComponents/DatePickerField";
import { hideModal } from "../redux/modalStore";
import axiosInstance from "../utils/utils";
import { getOrganizationData } from "../actions/organization";
import styles from "./LinkImprovementModal.module.scss";
import FormikCheckbox from "../components/FormikComponents/FormikCheckBox/FormikCheckBox";

const LinkImprovementModal = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { orgId } = useParams();
  const openedModal = useSelector((state) => state.modal.openedModal);
  const { improvementsData } = useSelector((state) => state.organization);

  const sessionToken = localStorage.getItem("session_token");

  const [activeTab, setActiveTab] = useState("existing");
  const [selectedImprovementId, setSelectedImprovementId] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [linkError, setLinkError] = useState("");

  const isOpen = openedModal?.name === "link_improvement_modal";

  const linkedFindings = useMemo(() => {
    if (!openedModal?.data) return [];
    return Array.isArray(openedModal.data)
      ? openedModal.data.filter(Boolean)
      : [openedModal.data];
  }, [openedModal?.data]);

  const defaultPrivacy = linkedFindings.some((finding) => finding?.private);

  const findingIds = useMemo(() => {
    return linkedFindings
      .map((finding) => {
        if (finding?.id) return finding.id;
        const match = finding?.key?.match(/\d+/);
        return match ? parseInt(match[0], 10) : null;
      })
      .filter(Boolean);
  }, [linkedFindings]);

  const improvementOptions = useMemo(() => {
    const options = Object.values(improvementsData || {}).map((improvement) => {
      const name = improvement.name || improvement.description;
      const key = improvement.key || t("linkImprovementModal.untitledProject");
      return {
        id: improvement.id,
        label: name ? `${name} (${key})` : t("linkImprovementModal.untitledProject"),
        sortKey: key,
      };
    });

    return options
      .sort((a, b) => {
        // Sort by numeric value if present, otherwise lexicographically, descending
        const numA = parseInt((a.sortKey || "").replace(/\D+/g, ""), 10);
        const numB = parseInt((b.sortKey || "").replace(/\D+/g, ""), 10);

        if (!isNaN(numA) && !isNaN(numB) && numA !== numB) {
          return numB - numA;
        }
        return (b.sortKey || "").localeCompare(a.sortKey || "");
      })
      .map(({ sortKey, ...rest }) => rest);
  }, [improvementsData, t]);

  useEffect(() => {
    if (isOpen) {
      setActiveTab("existing");
      setSelectedImprovementId("");
      setLinkError("");
    }
  }, [isOpen]);

  const handleClose = () => {
    if (isSubmitting) return;
    dispatch(hideModal());
    setSelectedImprovementId("");
    setLinkError("");
    setActiveTab("existing");
  };

  const formatDate = (inputDate) => {
    if (!inputDate) {
      return new Date().toISOString().split("T")[0];
    }
    return new Date(inputDate).toISOString().split("T")[0];
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string().required(
      t("linkImprovementModal.validation.nameRequired"),
    ),
    description: Yup.string().required(
      t("linkImprovementModal.validation.descriptionRequired"),
    ),
  });

  const handleLinkExisting = async () => {
    if (!selectedImprovementId) {
      setLinkError(t("linkImprovementModal.validation.improvementRequired"));
      return;
    }
    if (!findingIds.length) {
      setLinkError(t("linkImprovementModal.validation.findingMissing"));
      return;
    }

    setIsSubmitting(true);
    setLinkError("");

    try {
      await axiosInstance.post(
        `/nocodb/link_findings?org_id=${orgId}&link_target=Improvement&target_id=${selectedImprovementId}`,
        findingIds,
        {
          headers: { "session-token": sessionToken },
        },
      );

      await dispatch(
        getOrganizationData(
          orgId,
          sessionToken,
          navigate,
          false,
          true,
          true,
          false,
          false,
          false,
        ),
      );

      handleClose();
    } catch (error) {
      console.error("Error linking improvement:", error);
      setLinkError(t("linkImprovementModal.errors.linkFailed"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateImprovement = async (values, actions) => {
    if (!findingIds.length) {
      actions.setSubmitting(false);
      setLinkError(t("linkImprovementModal.validation.findingMissing"));
      return;
    }
    setIsSubmitting(true);
    setLinkError("");

    try {
      const createResponse = await axiosInstance.post(
        `/nocodb/improvements?org_id=${orgId}`,
        [
          {
            name: values.name,
            description: values.description,
            date: formatDate(values.date),
            private: values.private,
          },
        ],
        {
          headers: { "session-token": sessionToken },
        },
      );

      const targetId = createResponse.data;

      await axiosInstance.post(
        `/nocodb/link_findings?org_id=${orgId}&link_target=Improvement&target_id=${targetId}`,
        findingIds,
        {
          headers: { "session-token": sessionToken },
        },
      );

      if (findingIds && findingIds.length > 0) {
        const updateFindingStatusPromises = findingIds.map((findingId) =>
          axiosInstance.patch(
            `/nocodb/findings?org_id=${orgId}`,
            [
              {
                id: findingId,
                status: "In Progress",
              },
            ],
            {
              headers: {
                "Content-Type": "application/json",
                "session-token": sessionToken,
              },
            },
          ),
        );

        await Promise.all(updateFindingStatusPromises);
      }

      await dispatch(
        getOrganizationData(
          orgId,
          sessionToken,
          navigate,
          false,
          true,
          true,
          false,
          false,
          false,
        ),
      );

      actions.resetForm();
      handleClose();
    } catch (error) {
      console.error("Error creating and linking improvement:", error);
      setLinkError(t("linkImprovementModal.errors.createFailed"));
    } finally {
      setIsSubmitting(false);
      actions.setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal
      name="link_improvement_modal"
      title={t("linkImprovementModal.title")}
      subtitle={t("linkImprovementModal.subtitle")}
      onClose={handleClose}
      width="700px"
    >
      <div className={styles.modalContent}>
        <div className={styles.tabSwitcher} role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "existing"}
            className={classNames(styles.tabButton, {
              [styles.activeTab]: activeTab === "existing",
            })}
            onClick={() => {
              setActiveTab("existing");
              setLinkError("");
            }}
          >
            {t("linkImprovementModal.linkExisting")}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "new"}
            className={classNames(styles.tabButton, {
              [styles.activeTab]: activeTab === "new",
            })}
            onClick={() => {
              setActiveTab("new");
              setLinkError("");
            }}
          >
            {t("linkImprovementModal.createNew")}
          </button>
        </div>

        {activeTab === "existing" && (
          <div className={styles.section}>
            <label className={styles.label}>
              {t("linkImprovementModal.selectLabel")}
            </label>
            <Select
              text={t("linkImprovementModal.selectPlaceholder")}
              value={selectedImprovementId}
              onChange={(value) => {
                setSelectedImprovementId(value || "");
                setLinkError("");
              }}
              options={improvementOptions}
              className={styles.selectControl}
              variant="modal"
            />
          </div>
        )}

        {activeTab === "new" && (
          <Formik
            initialValues={{
              name: "",
              description: "",
              date: "",
              private: defaultPrivacy,
            }}
            enableReinitialize
            validationSchema={validationSchema}
            onSubmit={handleCreateImprovement}
          >
            {({ isSubmitting: isCreating, isValid, dirty }) => (
              <Form className={styles.form}>
                <div className={styles.fieldGroup}>
                  <label className={styles.label} htmlFor="name">
                    {t("linkImprovementModal.nameLabel")}
                  </label>
                  <Field
                    name="name"
                    id="name"
                    component={Input}
                    placeholder={t("linkImprovementModal.namePlaceholder")}
                    className={styles.textInput}
                    wrapperClassName={styles.fullWidthWrapper}
                  />
                </div>
                <div className={styles.fieldGroup}>
                  <label className={styles.label} htmlFor="description">
                    {t("linkImprovementModal.descriptionLabel")}
                  </label>
                  <Field
                    name="description"
                    id="description"
                    component={Textarea}
                    placeholder={t("linkImprovementModal.descriptionPlaceholder")}
                    className={styles.textArea}
                  />
                </div>
                <div className={styles.inlineFields}>
                  <div className={styles.dateField}>
                    <label className={styles.label} htmlFor="date">
                      {t("linkImprovementModal.dateLabel")}
                    </label>
                    <Field
                      name="date"
                      component={DatePickerField}
                      validateOnBlur={true}
                      validateOnChange={false}
                    />
                  </div>
                  {/* <div className={styles.checkboxField}>
                    <FormikCheckbox
                      name="private"
                      label={t("linkImprovementModal.privateLabel")}
                      trueValue={true}
                      falseValue={false}
                    />
                  </div> */}
                </div>

                {linkError && (
                  <div className={styles.formError}>{linkError}</div>
                )}

                <div className={styles.footer}>
                  <button
                    type="button"
                    className={styles.cancelButton}
                    onClick={handleClose}
                    disabled={isSubmitting || isCreating}
                  >
                    {t("linkImprovementModal.cancel")}
                  </button>
                  <button
                    type="submit"
                    className={styles.primaryButton}
                    disabled={isSubmitting || isCreating || !dirty || !isValid}
                  >
                    {isCreating || isSubmitting
                      ? t("linkImprovementModal.linkLoading")
                      : t("linkImprovementModal.linkCta")}
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        )}

        {activeTab === "existing" && (
          <>
            {linkError && <div className={styles.formError}>{linkError}</div>}
            <div className={styles.footer}>
              <button
                type="button"
                className={styles.cancelButton}
                onClick={handleClose}
                disabled={isSubmitting}
              >
                {t("linkImprovementModal.cancel")}
              </button>
              <button
                type="button"
                className={styles.primaryButton}
                onClick={handleLinkExisting}
                disabled={isSubmitting || !selectedImprovementId}
              >
                {isSubmitting
                  ? t("linkImprovementModal.linkLoading")
                  : t("linkImprovementModal.linkCta")}
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default LinkImprovementModal;
